import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Avatar,
  Divider,
  CircularProgress,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  IconButton
} from '@mui/material';
import {
  Edit as EditIcon,
  Language as WebIcon,
  Phone as PhoneIcon,
  Add as AddIcon,
  LinkOff as UnlinkIcon,
  CloudUpload as UploadIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api, { getApiBaseUrl, apiUpload } from '../../utils/api';
import { getAuthAccessToken } from '../../utils/demoPreviewStorage';
import { websiteHref } from '../../utils/websiteUrl';
import { useAuth } from '../../context/AuthContext';
import { DetailPageHeader, DetailTwoColumnGrid } from '../../components/common/DetailPageLayout';
import RecordAuditMeta from '../../components/common/RecordAuditMeta';
import useDocumentTypes, { labelForDocumentType, defaultDocumentTypeCode } from '../../hooks/useDocumentTypes';

function documentDownloadUrl(documentId) {
  const base = String(getApiBaseUrl() || '').replace(/\/+$/, '');
  return `${base}/documents/${documentId}/download`;
}

const AccountDetails = ({ detailId: detailIdProp, embedded, onCloseEmbedded } = {}) => {
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const id = detailIdProp ?? routeId;
  const { hasPermission } = useAuth();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  const [addContactOpen, setAddContactOpen] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [contactPickOptions, setContactPickOptions] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [newContactRelationship, setNewContactRelationship] = useState('primary');
  const [linkingContact, setLinkingContact] = useState(false);

  const [addDocOpen, setAddDocOpen] = useState(false);
  const [docForm, setDocForm] = useState({ title: '', document_type: 'other', file: null });
  const [docUploading, setDocUploading] = useState(false);
  const { documentTypes, loading: docTypesLoading } = useDocumentTypes();

  useEffect(() => {
    if (!documentTypes.length) return;
    const codes = new Set(documentTypes.map((t) => t.code));
    const def = defaultDocumentTypeCode(documentTypes);
    setDocForm((f) => (codes.has(f.document_type) ? f : { ...f, document_type: def }));
  }, [documentTypes]);

  const fetchAccount = async () => {
    try {
      const response = await api.get(`/accounts/${id}`);
      setAccount(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch');
      if (embedded && typeof onCloseEmbedded === 'function') onCloseEmbedded();
      else navigate('/accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchAccount();
  }, [id]);

  useEffect(() => {
    if (!addContactOpen) return;
    const t = setTimeout(() => {
      (async () => {
        try {
          const r = await api.get('/contacts', {
            params: { limit: 50, search: contactSearch || undefined, status: 'active' }
          });
          setContactPickOptions(r.data.data || []);
        } catch (e) {
          console.error(e);
        }
      })();
    }, 300);
    return () => clearTimeout(t);
  }, [addContactOpen, contactSearch]);

  const linkedContactIds = new Set((account?.Contacts || []).map((c) => c.id));

  const handleLinkContact = async () => {
    if (!selectedContact) {
      toast.error('Select a contact');
      return;
    }
    if (linkedContactIds.has(selectedContact.id)) {
      toast.error('That contact is already linked to this account');
      return;
    }
    setLinkingContact(true);
    try {
      await api.post(`/contacts/${selectedContact.id}/accounts`, {
        account_id: id,
        relationship_type: newContactRelationship
      });
      toast.success('Contact linked to account');
      setAddContactOpen(false);
      setSelectedContact(null);
      setContactSearch('');
      setNewContactRelationship('primary');
      fetchAccount();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to link contact');
    } finally {
      setLinkingContact(false);
    }
  };

  const handleUnlinkContact = async (contact) => {
    if (!window.confirm(`Remove “${[contact.first_name, contact.last_name].filter(Boolean).join(' ')}” from this account?`)) {
      return;
    }
    try {
      await api.delete(`/contacts/${contact.id}/accounts/${id}`);
      toast.success('Contact removed from account');
      fetchAccount();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to remove');
    }
  };

  const handleDocumentDownload = async (doc) => {
    try {
      const token = getAuthAccessToken();
      const response = await fetch(documentDownloadUrl(doc.id), {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) {
        toast.error('Failed to download');
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name || doc.title;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      toast.error('Failed to download');
    }
  };

  const handleAddDocument = async () => {
    if (!docForm.file) {
      toast.error('Choose a file');
      return;
    }
    setDocUploading(true);
    try {
      await apiUpload('/documents', docForm.file, {
        title: docForm.title || docForm.file.name.replace(/\.[^.]+$/, ''),
        document_type: docForm.document_type,
        entity_type: 'account',
        entity_id: id
      });
      toast.success('Document uploaded');
      setAddDocOpen(false);
      setDocForm({ title: '', document_type: defaultDocumentTypeCode(documentTypes), file: null });
      fetchAccount();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Upload failed');
    } finally {
      setDocUploading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!account) return null;

  const typeColors = {
    client: '#6366f1',
    vendor: '#ec4899',
    both: '#10b981',
    hosting_company: '#0ea5e9'
  };
  const typeLabels = {
    client: 'CLIENT',
    vendor: 'VENDOR',
    both: 'BOTH',
    hosting_company: 'HOSTING COMPANY'
  };
  const initial = (account.company_name || '?').trim().charAt(0).toUpperCase();

  return (
    <Box>
      <DetailPageHeader
        title="Account Details"
        onBack={embedded && typeof onCloseEmbedded === 'function' ? onCloseEmbedded : () => navigate('/accounts')}
        actions={
          hasPermission('accounts_modify') ? (
            <Button variant="contained" startIcon={<EditIcon />} onClick={() => navigate(`/accounts/${id}/edit`)}>
              Edit
            </Button>
          ) : null
        }
      />

      <DetailTwoColumnGrid
        sidebar={
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Avatar sx={{ width: 100, height: 100, mx: 'auto', mb: 2, bgcolor: 'primary.main', fontSize: '2rem' }}>
                {initial}
              </Avatar>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {account.company_name}
              </Typography>
              {account.account_code && (
                <Chip label={account.account_code} size="small" sx={{ mt: 1, fontFamily: 'monospace', fontWeight: 600 }} />
              )}
              <Chip
                label={typeLabels[account.account_type] || account.account_type?.toUpperCase()}
                size="small"
                sx={{
                  mt: 1,
                  bgcolor: `${typeColors[account.account_type] || '#64748b'}20`,
                  color: typeColors[account.account_type] || '#64748b'
                }}
              />
              {account.account_type === 'hosting_company' && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, px: 0.5 }}>
                  * Hosting company: organization hosting this software / tenant. Multiple hosting companies are allowed in
                  this tenant.
                </Typography>
              )}
              {account.status && (
                <Chip
                  label={account.status?.toUpperCase()}
                  size="small"
                  sx={{
                    mt: 1,
                    ml: 1,
                    bgcolor: account.status === 'active' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(107, 114, 128, 0.15)',
                    color: account.status === 'active' ? '#10b981' : '#6b7280'
                  }}
                />
              )}
              <Divider sx={{ my: 3 }} />
              <List dense sx={{ textAlign: 'left' }}>
                {account.industry && (
                  <ListItem>
                    <ListItemText primary="Industry" secondary={account.industry} />
                  </ListItem>
                )}
                {account.website && (
                  <ListItem
                    component="a"
                    href={websiteHref(account.website)}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: 'inherit' }}
                  >
                    <WebIcon sx={{ mr: 2, color: 'text.secondary' }} />
                    <ListItemText primary="Website" secondary={account.website} />
                  </ListItem>
                )}
                {account.phone && (
                  <ListItem>
                    <PhoneIcon sx={{ mr: 2, color: 'text.secondary' }} />
                    <ListItemText primary={account.phone} />
                  </ListItem>
                )}
              </List>
              <RecordAuditMeta
                creator={account.creator}
                updater={account.updater}
                created_at={account.created_at}
                updated_at={account.updated_at}
              />
            </CardContent>
          </Card>
        }
      >
        <Card>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
          >
            <Tab label="Overview" />
            <Tab label="Jobs" />
            <Tab label="Contacts" />
            <Tab label="Documents" />
          </Tabs>
          <CardContent>
            {activeTab === 0 && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Address
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {account.address_line1 || 'N/A'}
                </Typography>
                <Typography variant="body1">
                  {[account.city, account.state, account.zip_code].filter(Boolean).join(', ') || '—'}
                </Typography>
              </Box>
            )}
            {activeTab === 1 && (
              <List>
                {account.jobs?.length === 0 ? (
                  <Typography color="text.secondary" textAlign="center" py={4}>
                    No jobs
                  </Typography>
                ) : (
                  account.jobs?.map((job) => (
                    <ListItem key={job.id} divider sx={{ cursor: 'pointer' }} onClick={() => navigate(`/jobs/${job.id}`)}>
                      <ListItemText primary={job.title} secondary={`${job.job_code} • ${job.status}`} />
                      <Chip label={job.priority} size="small" />
                    </ListItem>
                  ))
                )}
              </List>
            )}
            {activeTab === 2 && (
              <Box>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    People associated with this company. Link an existing contact record (same as from the contact’s Accounts tab).
                  </Typography>
                  {hasPermission('contacts_modify') && hasPermission('contacts_view') && (
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      variant="contained"
                      onClick={() => {
                        setAddContactOpen(true);
                        setSelectedContact(null);
                        setContactSearch('');
                      }}
                    >
                      Add contact
                    </Button>
                  )}
                </Box>
                {(!account.Contacts || account.Contacts.length === 0) && (
                  <Typography color="text.secondary" textAlign="center" py={4}>
                    No contacts linked yet. Use &quot;Add contact&quot; to connect a person to this account.
                  </Typography>
                )}
                {account.Contacts?.length > 0 && (
                  <List dense disablePadding>
                    {account.Contacts.map((c) => (
                      <ListItem
                        key={c.id}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          mb: 1,
                          alignItems: 'flex-start'
                        }}
                        secondaryAction={
                          hasPermission('contacts_modify') ? (
                            <IconButton size="small" aria-label="Unlink contact" onClick={() => handleUnlinkContact(c)}>
                              <UnlinkIcon fontSize="small" />
                            </IconButton>
                          ) : null
                        }
                      >
                        <ListItemText
                          primary={
                            <Button
                              variant="text"
                              sx={{ p: 0, minWidth: 0, justifyContent: 'flex-start', fontWeight: 600 }}
                              onClick={() => navigate(`/contacts/${c.id}`)}
                            >
                              {[c.first_name, c.last_name].filter(Boolean).join(' ')}
                            </Button>
                          }
                          secondary={
                            <Box>
                              <Typography component="span" variant="caption" color="text.secondary" display="block">
                                {c.email}
                                {c.contact_type ? ` · ${c.contact_type}` : ''}
                              </Typography>
                              {c.contact_accounts?.relationship_type && (
                                <Chip
                                  size="small"
                                  label={String(c.contact_accounts.relationship_type).replace(/_/g, ' ')}
                                  sx={{ mt: 0.5, height: 22, fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            )}
            {activeTab === 3 && (
              <Box>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Documents for this account (SOW, agreements, etc.). Same storage as the global Documents list.
                  </Typography>
                  {hasPermission('documents_add') && (
                    <Button
                      size="small"
                      startIcon={<UploadIcon />}
                      variant="contained"
                      onClick={() => {
                        setAddDocOpen(true);
                        setDocForm({ title: '', document_type: defaultDocumentTypeCode(documentTypes), file: null });
                      }}
                    >
                      Add document
                    </Button>
                  )}
                </Box>
                {(!account.documents || account.documents.length === 0) && (
                  <Typography color="text.secondary" textAlign="center" py={4}>
                    No documents yet. {hasPermission('documents_add') ? 'Use “Add document” to upload a file.' : ''}
                  </Typography>
                )}
                {account.documents?.length > 0 && (
                  <List>
                    {account.documents.map((doc) => {
                      const created = doc.created_at || doc.createdAt;
                      return (
                        <ListItem key={doc.id} divider>
                          <ListItemText
                            primary={doc.title}
                            secondary={`${labelForDocumentType(doc.document_type, documentTypes)} • ${created ? new Date(created).toLocaleString() : ''}`}
                          />
                          {hasPermission('documents_view') && (
                            <Button size="small" startIcon={<DownloadIcon />} onClick={() => handleDocumentDownload(doc)}>
                              Download
                            </Button>
                          )}
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </DetailTwoColumnGrid>

      <Dialog open={addContactOpen} onClose={() => setAddContactOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add contact to this account</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              options={contactPickOptions.filter((c) => !linkedContactIds.has(c.id))}
              getOptionLabel={(c) => `${[c.first_name, c.last_name].filter(Boolean).join(' ')} (${c.email})`}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={selectedContact}
              onChange={(_, v) => setSelectedContact(v)}
              onInputChange={(_, v) => setContactSearch(v)}
              renderInput={(params) => (
                <TextField {...params} label="Search contacts" placeholder="Name or email" />
              )}
            />
            <FormControl fullWidth size="small">
              <InputLabel id="rel-ac-label">Relationship</InputLabel>
              <Select
                labelId="rel-ac-label"
                label="Relationship"
                value={newContactRelationship}
                onChange={(e) => setNewContactRelationship(e.target.value)}
              >
                <MenuItem value="primary">Primary</MenuItem>
                <MenuItem value="secondary">Secondary</MenuItem>
                <MenuItem value="billing">Billing</MenuItem>
                <MenuItem value="technical">Technical</MenuItem>
                <MenuItem value="hr">HR</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddContactOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleLinkContact} disabled={linkingContact}>
            {linkingContact ? 'Linking…' : 'Link contact'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addDocOpen} onClose={() => !docUploading && setAddDocOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add document</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button variant="outlined" component="label" startIcon={<UploadIcon />}>
              {docForm.file ? docForm.file.name : 'Choose file'}
              <input
                type="file"
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  setDocForm((prev) => ({
                    ...prev,
                    file: f || null,
                    title: prev.title || (f ? f.name.replace(/\.[^.]+$/, '') : prev.title)
                  }));
                }}
              />
            </Button>
            <TextField
              label="Title"
              fullWidth
              value={docForm.title}
              onChange={(e) => setDocForm((p) => ({ ...p, title: e.target.value }))}
            />
            <FormControl fullWidth size="small">
              <InputLabel id="adoc-type">Document type</InputLabel>
              <Select
                labelId="adoc-type"
                label="Document type"
                value={docForm.document_type}
                onChange={(e) => setDocForm((p) => ({ ...p, document_type: e.target.value }))}
                disabled={docTypesLoading && documentTypes.length === 0}
              >
                {documentTypes.map((t) => (
                  <MenuItem key={t.code} value={t.code}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDocOpen(false)} disabled={docUploading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleAddDocument} disabled={docUploading}>
            {docUploading ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountDetails;
